import { useEffect, useState } from "react";
import { Lock, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { Button } from "./ui/button";

const LEVEL_STYLE = { none: "bg-slate-700 text-slate-300", view: "bg-sky-600 text-white", check: "bg-amber-500 text-white", manage: "bg-rose-600 text-white" };
const LEVEL_HELP = { none: "No access", view: "View only", check: "Do checks", manage: "Manage" };

const PermGrid = ({ role, modules, levels, onSaved }) => {
  const [perms, setPerms] = useState(role.perms);
  useEffect(() => { setPerms(role.perms); }, [role]);
  const save = async () => {
    try {
      await api.put(`/roles/${role.key}`, { perms });
      toast.success(`${role.label} permissions saved`);
      onSaved();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const dirty = JSON.stringify(perms) !== JSON.stringify(role.perms);
  return (
    <div className="space-y-2" data-testid={`perm-grid-${role.key}`}>
      {Object.entries(modules).map(([m, label]) => (
        <div key={m} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800/70 py-2 last:border-0">
          <span className="text-sm">{label}</span>
          <div className="flex overflow-hidden rounded-md border border-slate-700">
            {levels.map((l) => (
              <button key={l} type="button" disabled={role.locked} onClick={() => setPerms({ ...perms, [m]: l })} data-testid={`perm-${role.key}-${m}-${l}`}
                className={`h-8 px-2.5 text-[11px] font-semibold transition-colors sm:px-3 ${perms[m] === l ? LEVEL_STYLE[l] : "text-slate-500 hover:text-slate-200"}`}>{LEVEL_HELP[l]}</button>
            ))}
          </div>
        </div>
      ))}
      {!role.locked && <Button disabled={!dirty} onClick={save} className="mt-2 bg-rose-600 hover:bg-rose-700" data-testid={`perm-save-${role.key}`}>Save permissions</Button>}
    </div>
  );
};

const NewRole = ({ bases, labels, onCreated }) => {
  const [label, setLabel] = useState("");
  const [base, setBase] = useState("operator");
  const create = async () => {
    try {
      const r = await api.post("/roles", { label, base });
      toast.success(`Role ${label} created`);
      setLabel("");
      onCreated(r.data.key);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div className="space-y-2 border-t border-slate-800 p-3" data-testid="new-role-form">
      <input className="field h-10" placeholder="New role name, e.g. Machine Operator" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="new-role-name" />
      <select className="field h-10" value={base} onChange={(e) => setBase(e.target.value)} data-testid="new-role-base">
        {bases.map((b) => <option key={b} value={b}>Works like: {labels[b] || b}</option>)}
      </select>
      <Button disabled={!label.trim()} onClick={create} className="w-full bg-rose-600 hover:bg-rose-700" data-testid="new-role-create"><Plus className="mr-1 h-4 w-4" />Add role</Button>
    </div>
  );
};

export const RolesPanel = ({ onChange }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState("operator");
  const load = () => api.get("/roles").then((r) => { setData(r.data); onChange?.(); });
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const role = data.roles.find((r) => r.key === sel) || data.roles[0];
  const labels = Object.fromEntries(data.roles.map((r) => [r.key, r.label]));
  const remove = async () => {
    if (!window.confirm(`Delete role ${role.label}?`)) return;
    try {
      await api.delete(`/roles/${role.key}`);
      toast.success("Role deleted");
      setSel("operator");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]" data-testid="roles-panel">
      <div className="panel h-fit">
        {data.roles.map((r) => (
          <button key={r.key} onClick={() => setSel(r.key)} data-testid={`role-item-${r.key}`}
            className={`flex w-full items-center justify-between border-l-4 px-4 py-3 text-left text-sm transition-colors ${r.key === role.key ? "border-rose-500 bg-slate-800 font-semibold" : "border-transparent hover:bg-slate-800/60"}`}>
            <span>{r.label}{!r.builtin && <span className="ml-2 rounded bg-cyan-500/20 px-1.5 text-[10px] uppercase text-cyan-300">Custom</span>}</span>
            <span className="text-xs text-slate-500">{r.user_count} user{r.user_count === 1 ? "" : "s"}</span>
          </button>
        ))}
        <NewRole bases={data.base_roles} labels={labels} onCreated={(k) => { setSel(k); load(); }} />
      </div>
      <div className="panel p-4">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="font-display text-xl font-bold uppercase" data-testid="role-title">{role.label}</p>
            <p className="text-xs text-slate-400">
              {role.locked ? <><Lock className="mr-1 inline h-3 w-3" />Admins always have full access.</> : role.builtin ? "Built-in role - permissions can be changed." : `Custom role - other actions (approvals, workflows) follow ${labels[role.base] || role.base}.`}
            </p>
          </div>
          {!role.builtin && <Button variant="ghost" className="text-red-300" onClick={remove} data-testid="role-delete"><Trash2 className="mr-1 h-4 w-4" />Delete</Button>}
        </div>
        <PermGrid role={role} modules={data.modules} levels={data.levels} onSaved={load} />
      </div>
    </div>
  );
};
